import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Users, Zap, Clock, FileCheck, CheckCircle2 } from 'lucide-react';
import SEO from '../../components/SEO';

const CyberSecurity = () => {
  return (
    <>
      <SEO 
        title="Cybersecurity Services - Enterprise Security Solutions"
        description="Protect your business with comprehensive cybersecurity services including threat detection, risk assessment, compliance management, and 24/7 security monitoring from Abacies Logiciels."
        keywords="cybersecurity, information security, threat detection, security compliance, penetration testing, security audit, risk assessment, data protection, network security"
      />

      {/* Hero Section */}
      <section className="relative pt-10 pb-10 md:pt-10 md:pb-10 bg-gradient-to-br from-charcoal via-graphite to-charcoal overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-primary-red rounded-full filter blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-coral-mist rounded-full filter blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="page-container relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full mb-6 animate-fade-in">
              <svg className="w-5 h-5 text-coral-mist mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <span className="text-sm font-semibold">Enterprise Security Solutions</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 animate-slide-up">
              Cybersecurity Services for <span className="text-gradient bg-gradient-to-r from-coral-mist to-primary-red bg-clip-text text-transparent">Modern Enterprises</span>
            </h1>
            
            <p className="text-xl text-cool-grey leading-relaxed mb-8 animate-slide-up" style={{ animationDelay: '0.1s' }}>
              Protect your digital assets with comprehensive security solutions designed to defend against evolving cyber threats, ensure compliance, and maintain business continuity.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <Link to="/contact" className="bg-primary-red text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-deep-garnet transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105">
                Request Security Assessment
              </Link>
              <a href="#services" className="bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white/20 transition-all duration-300">
                Explore Services
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-5 bg-white border-b border-platinum">
        <div className="page-container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold text-primary-red mb-2">99.9%</div>
              <div className="text-sm text-titanium">Threat Detection Rate</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-primary-red mb-2">24/7</div>
              <div className="text-sm text-titanium">Security Monitoring</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-primary-red mb-2">&lt;15min</div>
              <div className="text-sm text-titanium">Average Response Time</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-primary-red mb-2">100%</div>
              <div className="text-sm text-titanium">Compliance Assurance</div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Security Services */}
      <section id="services" className="py-5 md:py-5 bg-alabaster">
        <div className="page-container">
          <div className="text-center mb-16">
            <h2 className="section-title">Comprehensive Security Services</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              End-to-end cybersecurity solutions to protect your organization from threats and ensure regulatory compliance
            </p>
          </div>

          <div className="max-w-5xl mx-auto space-y-8">
            {[
              {
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                ),
                title: 'Threat Detection & Response',
                description: 'Advanced threat intelligence, real-time monitoring, and rapid incident response to identify and neutralize security threats before they impact your business.',
                features: ['Real-time monitoring', 'AI-powered detection', 'Rapid response team']
              },
              {
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                ),
                title: 'Security Assessment & Audit',
                description: 'Comprehensive security audits, vulnerability assessments, and penetration testing to identify weaknesses and strengthen your security posture.',
                features: ['Vulnerability scanning', 'Penetration testing', 'Security audits']
              },
              {
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                ),
                title: 'Compliance Management',
                description: 'Ensure adherence to regulatory requirements including GDPR, HIPAA, SOC 2, ISO 27001, and industry-specific standards with our compliance expertise.',
                features: ['GDPR compliance', 'HIPAA certification', 'SOC 2 reporting']
              },
              {
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                ),
                title: 'Cloud Security',
                description: 'Protect your cloud infrastructure with comprehensive security solutions for AWS, Azure, and Google Cloud, including configuration management and access controls.',
                features: ['Multi-cloud security', 'Configuration management', 'Access controls']
              },
              {
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
                ),
                title: 'Network Security',
                description: 'Implement robust network security measures including firewalls, intrusion detection systems, VPNs, and network segmentation to protect your infrastructure.',
                features: ['Firewall management', 'IDS/IPS systems', 'Network segmentation']
              },
              {
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                ),
                title: 'Identity & Access Management',
                description: 'Control and monitor user access with advanced IAM solutions, multi-factor authentication, single sign-on, and privileged access management.',
                features: ['Multi-factor auth', 'Single sign-on', 'Access monitoring']
              },
              {
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                ),
                title: 'Data Protection & Encryption',
                description: 'Safeguard sensitive data with enterprise-grade encryption, data loss prevention, and secure backup solutions to ensure data confidentiality and integrity.',
                features: ['End-to-end encryption', 'Data loss prevention', 'Secure backups']
              },
              {
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                ),
                title: 'Security Awareness Training',
                description: 'Educate your workforce with comprehensive security awareness programs, phishing simulations, and best practices training to build a security-conscious culture.',
                features: ['Phishing simulations', 'Security workshops', 'Best practices']
              },
              {
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                ),
                title: 'Incident Response & Recovery',
                description: 'Rapid incident response services with 24/7 availability, forensic analysis, containment strategies, and disaster recovery planning to minimize business impact.',
                features: ['24/7 availability', 'Forensic analysis', 'Disaster recovery']
              }
            ].map((service, index) => (
              <div 
                key={index} 
                className={`group bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-500 border border-platinum overflow-hidden ${
                  index % 2 === 0 ? 'hover:border-primary-red/50' : 'hover:border-coral-mist/50'
                }`}
              >
                <div className={`flex flex-col ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} items-center`}>
                  {/* Icon Section */}
                  <div className={`w-full md:w-2/5 p-8 md:p-12 flex items-center justify-center ${
                    index % 2 === 0 
                      ? 'bg-gradient-to-br from-blush-light/20 to-coral-mist/10' 
                      : 'bg-gradient-to-br from-coral-mist/10 to-blush-light/20'
                  }`}>
                    <div className="relative">
                      {/* Decorative Circle */}
                      <div className="absolute inset-0 bg-primary-red/5 rounded-full scale-150 group-hover:scale-175 transition-transform duration-500"></div>
                      
                      {/* Icon Container */}
                      <div className="relative w-24 h-24 md:w-32 md:h-32 bg-white rounded-2xl shadow-lg flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-all duration-500">
                        <svg className="w-12 h-12 md:w-16 md:h-16 text-primary-red group-hover:text-deep-garnet transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          {service.icon}
                        </svg>
                      </div>
                      
                      {/* Number Badge */}
                      <div className="absolute -top-3 -right-3 w-10 h-10 bg-primary-red text-white rounded-full flex items-center justify-center font-bold shadow-lg group-hover:scale-110 transition-transform duration-300">
                        {(index + 1).toString().padStart(2, '0')}
                      </div>
                    </div>
                  </div>

                  {/* Content Section */}
                  <div className="w-full md:w-3/5 p-8 md:p-10">
                    <h3 className="text-2xl md:text-3xl font-bold mb-4 text-charcoal group-hover:text-primary-red transition-colors duration-300">
                      {service.title}
                    </h3>
                    <p className="text-titanium leading-relaxed mb-6 text-base md:text-lg">
                      {service.description}
                    </p>
                    
                    {/* Feature Tags */}
                    <div className="flex flex-wrap gap-2">
                      {service.features.map((feature, fIndex) => (
                        <span 
                          key={fIndex} 
                          className="inline-flex items-center px-3 py-1.5 bg-blush-light/30 text-primary-red text-sm rounded-full border border-primary-red/20 group-hover:bg-primary-red/10 group-hover:border-primary-red/40 transition-all duration-300"
                        >
                          <svg className="w-3 h-3 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <div className="text-center mt-12">
            <Link 
              to="/contact" 
              className="inline-flex items-center px-8 py-4 bg-primary-red text-white rounded-lg text-lg font-semibold hover:bg-deep-garnet transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
            >
              Get Custom Security Solution
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* Security Framework */}
      <section className="py-5 md:py-5 bg-white">
        <div className="page-container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="section-title">Our Security Framework</h2>
              <p className="text-xl text-titanium mb-8">
                We follow a comprehensive, multi-layered security approach that combines industry best practices, advanced technologies, and continuous monitoring to protect your organization.
              </p>
              
              <div className="space-y-6">
                {[
                  {
                    step: '01',
                    title: 'Assessment & Planning',
                    description: 'Comprehensive security audit to identify vulnerabilities and develop a tailored security strategy'
                  },
                  {
                    step: '02',
                    title: 'Implementation',
                    description: 'Deploy security controls, tools, and processes across your infrastructure with minimal disruption'
                  },
                  {
                    step: '03',
                    title: 'Monitoring & Detection',
                    description: '24/7 security monitoring with AI-powered threat detection and real-time alerting'
                  },
                  {
                    step: '04',
                    title: 'Response & Recovery',
                    description: 'Rapid incident response and recovery protocols to minimize impact and restore operations'
                  },
                  {
                    step: '05',
                    title: 'Continuous Improvement',
                    description: 'Regular assessments, updates, and optimization to maintain robust security posture'
                  }
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-4 group">
                    <div className="flex-shrink-0 w-12 h-12 bg-primary-red/10 rounded-lg flex items-center justify-center text-primary-red font-bold group-hover:bg-primary-red group-hover:text-white transition-all duration-300">
                      {item.step}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-2 text-charcoal">{item.title}</h3>
                      <p className="text-titanium">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="bg-gradient-to-br from-alabaster to-pearl-white rounded-2xl p-8 shadow-lg">
                <div className="space-y-6">
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-platinum">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold text-charcoal">Security Layers</h4>
                      <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="space-y-3">
                      {['Network Security', 'Application Security', 'Data Security', 'Endpoint Security', 'Cloud Security'].map((layer, index) => (
                        <div key={index} className="flex items-center justify-between">
                          <span className="text-sm text-titanium">{layer}</span>
                          <div className="flex-1 mx-4 h-2 bg-cool-grey/20 rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-primary-red to-coral-mist rounded-full" style={{ width: `${95 - index * 5}%` }}></div>
                          </div>
                          <span className="text-sm font-semibold text-primary-red">{95 - index * 5}%</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-xl shadow-sm border border-platinum">
                    <h4 className="font-semibold text-charcoal mb-4">Threat Protection</h4>
                    <div className="grid grid-cols-2 gap-4">
                      {[
                        { label: 'Malware', value: '99.9%' },
                        { label: 'Phishing', value: '98.7%' },
                        { label: 'DDoS', value: '99.5%' },
                        { label: 'Zero-Day', value: '97.2%' }
                      ].map((item, index) => (
                        <div key={index} className="text-center p-3 bg-blush-light/20 rounded-lg">
                          <div className="text-2xl font-bold text-primary-red mb-1">{item.value}</div>
                          <div className="text-xs text-titanium">{item.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance & Certifications */}
      <section className="py-5 md:py-5 bg-charcoal text-white">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Compliance & Certifications</h2>
            <p className="text-xl text-cool-grey max-w-3xl mx-auto">
              We help you achieve and maintain compliance with industry standards and regulatory requirements
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {[
              { name: 'ISO 27001', description: 'Information Security Management' },
              { name: 'SOC 2 Type II', description: 'Service Organization Controls' },
              { name: 'GDPR', description: 'Data Protection Regulation' },
              { name: 'HIPAA', description: 'Healthcare Data Security' },
              { name: 'PCI DSS', description: 'Payment Card Industry' },
              { name: 'NIST', description: 'Cybersecurity Framework' },
              { name: 'FedRAMP', description: 'Federal Risk Authorization' },
              { name: 'CMMC', description: 'Cybersecurity Maturity Model' }
            ].map((cert, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/10 hover:bg-white/10 hover:border-coral-mist/50 transition-all duration-300">
                <div className="w-12 h-12 bg-primary-red/20 rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-coral-mist" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                </div>
                <h3 className="font-semibold mb-2">{cert.name}</h3>
                <p className="text-sm text-cool-grey">{cert.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-5 md:py-5 bg-alabaster">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="section-title">Why Choose Abacies for Cybersecurity</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              Partner with security experts who understand the evolving threat landscape and your business needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Proactive Threat Intelligence',
                description: 'Stay ahead of threats with our advanced threat intelligence platform and security research team',
                icon: Shield
              },
              {
                title: 'Experienced Security Team',
                description: 'Certified security professionals with decades of combined experience across industries',
                icon: Users
              },
              {
                title: 'Advanced Technology Stack',
                description: 'Enterprise-grade security tools and AI-powered threat detection systems',
                icon: Zap
              },
              {
                title: '24/7 Security Operations',
                description: 'Round-the-clock monitoring and rapid response from our Security Operations Center',
                icon: Clock
              },
              {
                title: 'Industry Compliance Expertise',
                description: 'Deep knowledge of regulatory requirements and industry-specific security standards',
                icon: FileCheck
              },
              {
                title: 'Proven Track Record',
                description: 'Trusted by Fortune 500 companies and organizations in highly regulated industries',
                icon: CheckCircle2
              }
            ].map((item, index) => {
              const IconComponent = item.icon;
              return (
                <div key={index} className="bg-white p-8 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 border border-platinum group">
                  <div className="w-14 h-14 bg-blush-light/40 rounded-xl flex items-center justify-center mb-4 group-hover:bg-primary-red/10 transition-all duration-300">
                    <IconComponent className="w-7 h-7 text-primary-red" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-charcoal">{item.title}</h3>
                  <p className="text-titanium">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-5 md:py-5 bg-gradient-to-br from-primary-red to-deep-garnet text-white">
        <div className="page-container text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
            Secure Your Business Today
          </h2>
          <p className="text-xl mb-8 opacity-90 max-w-3xl mx-auto">
            Don't wait for a security breach. Get a comprehensive security assessment and discover how we can protect your organization from cyber threats.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-white text-primary-red px-8 py-4 rounded-lg text-lg font-semibold hover:bg-pearl-white transition-all duration-300 shadow-lg hover:scale-105">
              Schedule Security Assessment
            </Link>
            <Link to="/services" className="bg-white/10 backdrop-blur-sm border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white/20 transition-all duration-300">
              View All Services
            </Link>
          </div>
          
          <div className="mt-12 pt-8 border-t border-white/20">
            <p className="text-sm opacity-75 mb-4">Trusted by leading organizations worldwide</p>
            <div className="flex flex-wrap justify-center gap-8 opacity-60">
              <div className="text-2xl font-bold">Fortune 500</div>
              <div className="text-2xl font-bold">Healthcare</div>
              <div className="text-2xl font-bold">Finance</div>
              <div className="text-2xl font-bold">Government</div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default CyberSecurity;